import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Account, Response, apiAbortController } from '@ui/src';
import api from '../../api/api';
import {
  Channel,
  Message,
  Employee,
  UploadResponse,
  Document,
  TopUpInstructions,
  ApplicationItem,
  ChannelStatus,
  CustomerDocumentType,
} from '../types';

export const messagesListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMainChannels: build.query<
      Response<Channel>,
      { skip?: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/channels?take=15${data.skip ? `&skip=${data.skip}` : ''}${
          data.query ? `&${data.query}` : ''
        }&channelType=MAIN`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMainChannelsByType: build.query<
      Response<Channel>,
      { skip?: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/channels?take=15&sortProp=updatedAt&sortDir=desc${
          data.skip ? `&skip=${data.skip}` : ''
        }${data.query ? `&${data.query}` : ''}&channelType=MAIN`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getRequestsChannels: build.query<
      Response<Channel>,
      { skip?: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/channels?take=15${data.skip ? `&skip=${data.skip}` : ''}${
          data.query ? `&${data.query}` : ''
        }&channelType=DEMO&channelType=APPLICATION&channelType=TRANSACTION`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getRequestsChannelsByType: build.query<
      Response<Channel>,
      { skip?: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/channels?take=15&sortProp=updatedAt&sortDir=desc${data.skip ? `&skip=${data.skip}` : ''}${
          data.query ? `&${data.query}` : ''
        }&channelType=DEMO&channelType=APPLICATION&channelType=TRANSACTION`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotifications: build.query<
      Response<Channel>,
      { skip?: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/channels?take=30${data.skip ? `&skip=${data.skip}` : ''}${
          data.query ? `&${data.query}` : ''
        }&channelType=NOTIFICATION`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMessagingEmployeesList: build.query<Response<Employee>, void>({
      query: () => ({
        url: '/v1/employees?take=9999',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMessages: build.query<
      Response<Message>,
      { id: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/channels/${
          data.id
        }/messages?take=20&sortProp=createdAt&sortDir=desc${
          data.cursor ? `&cursor=${data.cursor}` : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSelectedChannel: build.query<{ data: Channel }, string>({
      query: (id) => ({
        url: `/v1/channels/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    assignEmployeeToChat: build.mutation<
      { data: Channel },
      { body: { employeeId: string | null }; id: string }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeChatStatus: build.mutation<
      { data: Channel },
      { body: { status: ChannelStatus }; id: string }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    readMessages: build.mutation<
      { data: { count: number } },
      {
        body: {
          messages?: string[];
          createdAtFrom?: string;
          createdAtTo?: string;
          channelId?: string;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.id}/messages/batch-read`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unreadMessages: build.mutation<
      { data: { count: number } },
      { id: string; body: { messages: string[]; channelId: string } }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.id}/messages/batch-unread`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    messagingUpload: build.mutation({
      async queryFn(file: File, _queryApi, _extraOptions, fetchWithBQ) {
        const formData = new FormData();
        formData.append('files', file);

        const response = (await fetchWithBQ({
          url: '/v1/uploads',
          method: 'POST',
          body: formData,
        })) as { data: UploadResponse[] };

        return response;
      },
    }),
    sendFile: build.mutation<
      { data: Document[] },
      {
        body: {
          data: [
            {
              name: string;
              uploadId: string;
            },
          ];
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getChatDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getChannelDocuments: build.query<Response<Document>, string>({
      query: (id) => ({
        url: `/v1/channels/${id}/documents?all=true&sortProp=updatedAt&sortDir=desc`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserAccounts: build.query<Response<Account>, string>({
      query: (id) => ({
        url: `/v1/accounts?organizationId=${id}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMessagingTopUp: build.query<
      Response<{ companyName: string; id: string; visibleToClients: boolean }>,
      void
    >({
      query: () => ({
        url: '/v1/psp-partners?all=true&active=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTopUpCurrencies: build.query<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const dictionaries = await Promise.all([
          fetchWithBQ('/v1/currencies?all=true&active=true'),
          fetchWithBQ('/v1/currencies/base'),
        ]);

        return dictionaries
          ? {
              data: {
                currencies: dictionaries[0].data,
                baseCurrency: dictionaries[1].data,
              },
            }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    getMessagingTopUpInstructions: build.query<
      { data: TopUpInstructions },
      { id: string; currencyId: string; pspPartnerId: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/top-up?currencyId=${data.currencyId}&pspPartnerId=${data.pspPartnerId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMessagingTopUpInstructionPdf: build.query<
      { data: TopUpInstructions },
      { id: string; currencyId: string; pspPartnerId: string; index: number }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/top-up/${data.index}/pdf?currencyId=${data.currencyId}&pspPartnerId=${data.pspPartnerId}`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMessagingApplicationsList: build.query<
      Response<ApplicationItem>,
      string
    >({
      query: (query) => ({
        url: `/v1/clients-applications${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMessagingCurrentEmployee: build.query<{ data: Employee }, void>({
      query: () => ({
        url: '/v1/employees/whoami',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMessageDocument: build.query<
      { data: any },
      { channelId: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.channelId}/documents/${data.documentId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMessagesChannelInfo: build.query<
      {
        data: {
          mainChannels: number;
          requestChannels: number;
        };
      },
      string
    >({
      query: (query) => ({
        url: `/v1/channels/info${query ? `?${query}` : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    moveDocumentsToCustomer: build.mutation<
      { data: { count: 0 } },
      { body: { ids: string[]; category: CustomerDocumentType }; id: string }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.id}/documents/batch-move`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplication: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/clients-applications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientOrganization: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransaction: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetMainChannelsQuery,
  useLazyGetNotificationsQuery,
  useLazyGetRequestsChannelsQuery,
  useLazyGetMainChannelsByTypeQuery,
  useLazyGetRequestsChannelsByTypeQuery,
  useLazyGetMessagesQuery,
  useLazyGetMessagingEmployeesListQuery,
  useAssignEmployeeToChatMutation,
  useLazyGetSelectedChannelQuery,
  useReadMessagesMutation,
  useMessagingUploadMutation,
  useSendFileMutation,
  useLazyGetChatDocumentFileQuery,
  useLazyGetChannelDocumentsQuery,
  useLazyGetUserAccountsQuery,
  useLazyGetMessagingTopUpQuery,
  useLazyGetTopUpCurrenciesQuery,
  useLazyGetMessagingTopUpInstructionsQuery,
  useLazyGetMessagingTopUpInstructionPdfQuery,
  useLazyGetMessagingApplicationsListQuery,
  useChangeChatStatusMutation,
  useLazyGetMessagingCurrentEmployeeQuery,
  useLazyGetMessageDocumentQuery,
  useMoveDocumentsToCustomerMutation,
  useLazyGetMessagesChannelInfoQuery,
  useLazyGetApplicationQuery,
  useLazyGetClientOrganizationQuery,
  useLazyGetTransactionQuery,
  useUnreadMessagesMutation,
} = messagesListApi;

export const {
  endpoints: {
    getMainChannels,
    getRequestsChannels,
    getNotifications,
    getMainChannelsByType,
    getRequestsChannelsByType,
    getMessages,
    getMessagingEmployeesList,
    assignEmployeeToChat,
    getSelectedChannel,
    readMessages,
    messagingUpload,
    sendFile,
    getChatDocumentFile,
    getChannelDocuments,
    getUserAccounts,
    getMessagingTopUp,
    getTopUpCurrencies,
    getMessagingTopUpInstructions,
    getMessagingTopUpInstructionPdf,
    getMessagingApplicationsList,
    changeChatStatus,
    getMessagingCurrentEmployee,
    getMessageDocument,
    moveDocumentsToCustomer,
    getMessagesChannelInfo,
    getApplication,
    getClientOrganization,
    getTransaction,
    unreadMessages,
  },
} = messagesListApi;
